/**
 * The illuminance layer's server state: computed work-plane grids via GET /v1/viewer/illuminance.
 * Returns IlluminanceView[] with surfaces (rooms or building) + fixture counts + grid points.
 */
import { useQuery } from '@tanstack/react-query';
import { typedClient, throwOnServerError, type components } from '../../api';

/** Mapped illuminance view for use in the viewer. */
export interface IlluminanceRoom {
  scope: string;
  room_name: string | null;
  fixtures: number;
  points: [number, number, number][]; // (x, y, lux) on the work plane
  min_lux: number;
  avg_lux: number;
  worst_point: [number, number];
}

// The fixed color-scale reference (lux): 300 is the IES office target,
// so the same color means the same light level in every project.
export const LUX_SCALE_MAX = 300;

export function useIlluminance(projectId: string, enabled: boolean) {
  return useQuery<IlluminanceRoom[]>({
    queryKey: ['illuminance', projectId],
    enabled,
    queryFn: async () => {
      const response = await typedClient.GET('/v1/viewer/illuminance', {
        params: { query: { project_id: projectId } },
      });
      throwOnServerError(response);
      return ((response.data as components['schemas']['RoomIlluminance'][] | undefined) ?? []).map((view) => ({
        scope: view.surface.kind === 'room' ? view.surface.origin_id : 'building',
        room_name: view.surface.kind === 'room' ? view.surface.name : null,
        fixtures: view.fixtures,
        points: view.points,
        min_lux: view.min_lux,
        avg_lux: view.avg_lux,
        worst_point: view.worst_point,
      })) as IlluminanceRoom[];
    },
  });
}
